// ─── Types ───────────────────────────────────────────────────────────────────

export type AgentType = "AGENT" | "AGENTIC" | "AIAGENTS" | "aiagents";

export type AIProfile = "local" | "test" | "preprod" | "prod";

export interface PromptAgentProfileConfig {
  agentType: AgentType;
  agentBaseUrl: string;
  agenticBaseUrl: string;
  aiagentsBaseUrl: string;
  apiKey: string;
  agentId: string;
  /** Config toggle: whether to use async background job execution with polling (true) or normal sync execution (false) */
  isAsync: boolean;
  /** Job status polling interval timer in milliseconds (default: 5000ms / 5s) */
  pollIntervalMs: number;
  /** Maximum number of polling attempts before timing out (default: 60 attempts = 5 minutes at 5s) */
  maxPollAttempts?: number;
}

export interface ResolvedPromptAgentConfig {
  baseUrl: string;
  urlPath: string;
  jobResultPath: string;
  jobStatusPath: string;
  headers: Record<string, string>;
  agentType: AgentType;
  profile: AIProfile;
  isAsync: boolean;
  pollIntervalMs: number;
  maxPollAttempts: number;
}

// ─── Default Constants ────────────────────────────────────────────────────────

const DEFAULT_POLL_INTERVAL_MS = 5000; // 5 seconds
const DEFAULT_MAX_POLL_ATTEMPTS = 60; // 60 attempts * 5s = 5 minutes timeout

// ─── Profile Configurations ──────────────────────────────────────────────────

const PROFILE_CONFIG: Record<AIProfile, PromptAgentProfileConfig> = {
  local: {
    agentType: "AGENT",
    agentBaseUrl: "http://103.182.211.219:8070/v1",
    agenticBaseUrl: "http://103.182.211.219:8070/api/v1",
    aiagentsBaseUrl: "http://103.182.211.219:8070/api/v1",
    apiKey: "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=",
    agentId: "vibe-coding-tutor",
    isAsync: true,
    pollIntervalMs: 5000,
    maxPollAttempts: 60,
  },
  test: {
    agentType: "AGENT",
    agentBaseUrl: "http://103.182.211.219:8070/v1",
    agenticBaseUrl: "http://103.182.211.219:8070/api/v1",
    aiagentsBaseUrl: "http://103.182.211.219:8070/api/v1",
    apiKey: "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=",
    agentId: "vibe-coding-tutor",
    isAsync: true,
    pollIntervalMs: 5000,
    maxPollAttempts: 60,
  },
  preprod: {
    agentType: "AIAGENTS",
    agentBaseUrl: "https://vibecoding.topbrains.com/agent-prompt/v1",
    agenticBaseUrl: "https://vibecoding.topbrains.com/agent-prompt/api/v1",
    aiagentsBaseUrl: "https://aiagent.qspiders.com/api/v1",
    apiKey: "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=",
    agentId: "vibe-coding-tutor",
    isAsync: false,
    pollIntervalMs: 5000,
    maxPollAttempts: 60,
  },
  prod: {
    agentType: "AGENT",
    agentBaseUrl: "https://vibecoding.topbrains.com/agent-prompt/v1",
    agenticBaseUrl: "https://vibecoding.topbrains.com/agent-prompt/api/v1",
    aiagentsBaseUrl: "https://aiagent.qspiders.com/api/v1",
    apiKey: "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=",
    agentId: "vibe-coding-tutor",
    isAsync: false,
    pollIntervalMs: 5000,
    maxPollAttempts: 60,
  },
};

// ─── Default / Fallback ──────────────────────────────────────────────────────

const DEFAULT_PROFILE: AIProfile = "test";

/**
 * Resolves the appropriate agent_id based on language, framework, or explicit agentId.
 * e.g., 'react' -> 'vibe-coding-tutor-react'
 */
export function resolveAgentId(
  languageOrFramework?: string,
  customAgentId?: string
): string {
  if (customAgentId) return customAgentId;
  const tech = (languageOrFramework || "react").toLowerCase().trim();
  if (tech.includes("react")) return "vibe-coding-tutor-react";
  if (tech.includes("java")) return "vibe-coding-tutor-java";
  if (tech.includes("python")) return "vibe-coding-tutor-python";
  if (tech.includes("node") || tech.includes("express"))
    return "vibe-coding-tutor-node";
  if (tech.includes("javascript") || tech.includes("js"))
    return "vibe-coding-tutor-javascript";
  if (tech.includes("html") || tech.includes("css"))
    return "vibe-coding-tutor-frontend";
  return tech ? `vibe-coding-tutor-${tech}` : "vibe-coding-tutor";
}

/**
 * Resolves the active prompt agent configuration based on the VITE_AI_PROFILE
 * environment variable. Returns the base URL, URL path, headers, async execution toggle,
 * and poll interval for the active agent type and profile.
 */
export function getPromptAgentConfig(): ResolvedPromptAgentConfig {
  const profileEnv = (
    import.meta.env.VITE_AI_PROFILE || DEFAULT_PROFILE
  ).toLowerCase() as AIProfile;

  const profile: AIProfile = PROFILE_CONFIG[profileEnv]
    ? profileEnv
    : DEFAULT_PROFILE;

  const config = PROFILE_CONFIG[profile];
  const agentType = config.agentType;
  const upperAgentType = (agentType || "").toUpperCase();

  // Allow optional env overrides if specified
  const envAsync = import.meta.env.VITE_AI_RUN_ASYNC;
  const isAsync =
    envAsync !== undefined && envAsync !== ""
      ? String(envAsync).toLowerCase() === "true"
      : (config.isAsync ?? true);

  const envPollInterval = Number(import.meta.env.VITE_AI_POLL_INTERVAL_MS);
  const pollIntervalMs =
    !isNaN(envPollInterval) && envPollInterval > 0
      ? envPollInterval
      : config.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;

  const envMaxAttempts = Number(import.meta.env.VITE_AI_MAX_POLL_ATTEMPTS);
  const maxPollAttempts =
    !isNaN(envMaxAttempts) && envMaxAttempts > 0
      ? envMaxAttempts
      : config.maxPollAttempts || DEFAULT_MAX_POLL_ATTEMPTS;

  if (upperAgentType === "AIAGENTS") {
    return {
      baseUrl: config.aiagentsBaseUrl,
      urlPath: `/agents/run?agent_id=${config.agentId}`,
      jobResultPath: `/agents/run`,
      jobStatusPath: `/agents/run`,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
      },
      agentType,
      profile,
      isAsync,
      pollIntervalMs,
      maxPollAttempts,
    };
  }

  if (upperAgentType === "AGENTIC") {
    return {
      baseUrl: config.agenticBaseUrl,
      urlPath: `/agents/run?agent_id=${config.agentId}`,
      jobResultPath: `/agents/run`,
      jobStatusPath: `/agents/run`,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
      },
      agentType,
      profile,
      isAsync,
      pollIntervalMs,
      maxPollAttempts,
    };
  }

  // AGENT mode (default)
  return {
    baseUrl: config.agentBaseUrl,
    urlPath: "/run",
    jobResultPath: "/run",
    jobStatusPath: "/run",
    headers: {
      "Content-Type": "application/json",
    },
    agentType,
    profile,
    isAsync,
    pollIntervalMs,
    maxPollAttempts,
  };
}
